import React, { useState, useEffect, useRef } from 'react';
import { TextFieldProps, InputAdornment, IconButton } from '@mui/material';

import CoreInput from './CoreInput';
import CloseIcon from '../../icons/CloseIcon';
import { SearchIcon } from '../../icons';

interface CoreKeywordSearchProps extends Omit<TextFieldProps, 'onChange' | 'variant'> {
    label?: string;
    value?: string;
    variant?: 'standard' | 'outlined' | 'filled' | 'underline';
    onChange?: (value: string) => void;
    debounceMs?: number;
    showClearButton?: boolean;
    showSearchIcon?: boolean
}

const CoreKeywordSearch: React.FC<CoreKeywordSearchProps> = ({
    value = '',
    onChange,
    debounceMs = 800,
    showClearButton = true,
    showSearchIcon,
    ...rest
}) => {
    const [localValue, setLocalValue] = useState(value);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sync local value when external value changes
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        // Clear existing timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Set new debounced call
        debounceTimer.current = setTimeout(() => {
            onChange?.(newValue);
        }, debounceMs);
    };

    const handleClear = () => {
        // Clear any pending debounce
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        
        setLocalValue('');
        onChange?.('');
    };

    return (
        <CoreInput
            value={localValue}
            onChange={handleChange}
            InputProps={{
                ...rest.InputProps,
                endAdornment: showClearButton && localValue ? (
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="clear search"
                            onClick={handleClear}
                            edge="end"
                            size="small"
                            sx={{ 
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </InputAdornment>
                ) : showSearchIcon ? (
                    <InputAdornment position="end">
                        <SearchIcon />
                    </InputAdornment>
                ) : rest.InputProps?.endAdornment,
            }}
            {...rest}
        />
    );
};

export default CoreKeywordSearch;
